import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { formatNumber } from '../i18n/format';
import { MessageOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { compareTaskRecency } from '../lib/taskOrdering';
import { STATUS_CONFIG } from '../lib/taskStatus';
import type { Task } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import TaskRowActions from './TaskRowActions';
import surface from './ProjectSurface.module.css';
import styles from './PersonalTasksPage.module.css';

type TaskSectionName = 'in_progress' | 'attention' | 'completed';

const groups: Array<{ key: TaskSectionName; label: MessageDescriptor }> = [
  { key: 'in_progress', label: msg`In progress` },
  { key: 'attention', label: msg`Needs attention` },
  { key: 'completed', label: msg`Completed` },
];

function taskSection(task: Task): TaskSectionName {
  if (task.status === 'in_progress') return 'in_progress';
  if (task.status === 'done' || task.status === 'cancelled') return 'completed';
  return 'attention';
}

function responsibilityRank(task: Task) {
  const relationship = task.relationship;
  if (relationship?.assignee || relationship?.creator) return 0;
  if (relationship?.participant) return 1;
  if (relationship?.owned_agent) return 2;
  return 3;
}

function relationshipLabel(task: Task, i18n: I18n) {
  const relationship = task.relationship;
  if (!relationship) return null;
  const labels = [
    relationship.assignee && msg`Assigned to you`,
    relationship.creator && msg`Created by you`,
    relationship.participant && msg`You participate`,
    relationship.owned_agent && msg`Your agent`,
  ].filter((label): label is MessageDescriptor => !!label);
  return labels.map(label => i18n._(label)).join(' · ');
}

export default function PersonalTasksPage({
  activeThreadId,
  onTaskClick,
}: {
  activeThreadId?: string;
  onTaskClick: (task: Task) => void;
}) {
  const { i18n } = useLingui();
  // The inbox remembers only which authorized Tasks it loaded. The normalized
  // task store is the single source of entity data for list, thread, mutation,
  // and realtime updates; lanes are always derived from the live status.
  const [loadedTaskIds, setLoadedTaskIds] = useState<string[]>([]);
  const taskById = useTaskStore((state) => state.byTaskId);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    let stale = false;
    api.fetchMyTasks().then(({ tasks }) => {
      if (stale) return;
      for (const task of tasks) useTaskStore.getState().upsertTask(task);
      setLoadedTaskIds(tasks.map((task) => task.id));
    }).finally(() => { if (!stale) setReady(true); });
    return () => { stale = true; };
  }, []);
  const tasks = useMemo(
    () => loadedTaskIds
      .map((id) => taskById[id])
      .filter((task): task is Task => !!task)
      .filter((task) => {
        const text = `${task.title} ${task.channel?.guild_name ?? ''} ${task.channel?.channel_name ?? ''}`.toLowerCase();
        return (!query || text.includes(query.toLowerCase())) && (filter === 'all' || task.status === filter);
      })
      .sort((a, b) => responsibilityRank(a) - responsibilityRank(b) || compareTaskRecency(a, b)),
    [loadedTaskIds, taskById, query, filter],
  );
  const updateTask = (updated: Task) => useTaskStore.getState().upsertTask(updated);
  const removeTask = (taskId: string) => {
    useTaskStore.getState().removeTask(taskId);
    setLoadedTaskIds((ids) => ids.filter((id) => id !== taskId));
  };

  if (!ready) return <div className={surface.loading}><Trans>Loading your tasks…</Trans></div>;
  return (
    <main className={surface.pageRoot}>
      <header className={`${surface.pageContainer} ${surface.pageHeader} ${styles.header}`}>
        <div>
          <span className={surface.eyebrow}><Trans>Personal</Trans></span>
          <h1 className={surface.pageTitle}><Trans>My Tasks</Trans></h1>
        </div>
      </header>
      <div className={`${surface.pageContainer} ${styles.toolbar}`}>
        <input
          className={styles.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={i18n._(msg`Search tasks…`)}
          aria-label={i18n._(msg`Search tasks`)}
        />
        <div className={styles.filters}>
          {[
            ['all', i18n._(msg`All`)],
            ['open', i18n._(STATUS_CONFIG.open.label)],
            ['in_progress', i18n._(msg`In progress`)],
            ['in_review', i18n._(msg`In review`)],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`${styles.filter} ${filter === key ? styles.filterActive : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {query ? (
        <TaskList
          label={i18n._(msg`Search results`)}
          rows={tasks}
          activeThreadId={activeThreadId}
          onTaskClick={onTaskClick}
          onUpdated={updateTask}
          onDeleted={removeTask}
        />
      ) : (
        groups.map(({ key, label }) => {
          const rows = tasks.filter((task) => taskSection(task) === key);
          return (
            <section className={`${surface.pageContainer} ${surface.section} ${styles.section}`} key={key}>
              <h2>
                {i18n._(label)}
                {rows.length ? ` · ${formatNumber(i18n, rows.length)}` : ''}
              </h2>
              <div className={surface.borderedList}>
                {rows.length ? (
                  rows.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      active={task.thread_id === activeThreadId}
                      onClick={onTaskClick}
                      onUpdated={updateTask}
                      onDeleted={removeTask}
                    />
                  ))
                ) : (
                  <div className={surface.listEmpty}><Trans>No tasks here.</Trans></div>
                )}
              </div>
            </section>
          );
        })
      )}
    </main>
  );
}

function TaskList({
  label,
  rows,
  activeThreadId,
  onTaskClick,
  onUpdated,
  onDeleted,
}: {
  label: string;
  rows: Task[];
  activeThreadId?: string;
  onTaskClick: (task: Task) => void;
  onUpdated: (task: Task) => void;
  onDeleted: (taskId: string) => void;
}) {
  const { i18n } = useLingui();
  return (
    <section className={`${surface.pageContainer} ${surface.section} ${styles.section}`}>
      <h2>
        {label}
        {rows.length ? ` · ${formatNumber(i18n, rows.length)}` : ''}
      </h2>
      <div className={surface.borderedList}>
        {rows.length ? (
          rows.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              active={task.thread_id === activeThreadId}
              onClick={onTaskClick}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
            />
          ))
        ) : (
          <div className={surface.listEmpty}><Trans>No matching tasks.</Trans></div>
        )}
      </div>
    </section>
  );
}

function TaskRow({
  task,
  active,
  onClick,
  onUpdated,
  onDeleted,
}: {
  task: Task;
  active: boolean;
  onClick: (task: Task) => void;
  onUpdated: (task: Task) => void;
  onDeleted: (taskId: string) => void;
}) {
  const { i18n } = useLingui();
  const cfg = STATUS_CONFIG[task.status];
  const relationship = relationshipLabel(task, i18n);
  return (
    <div className={`${surface.listRow} ${surface.listRowInteractive} ${styles.row} ${active ? surface.listRowActive : ''}`}>
      <button
        type="button"
        className={styles.rowOpener}
        onClick={() => task.thread_id && onClick(task)}
        disabled={!task.thread_id}
        aria-label={task.thread_id ? i18n._(msg`Open ${task.title}`) : i18n._(msg`${task.title} has no task thread`)}
      >
        <span className={styles.status} style={{ background: cfg?.color ?? '#a1a1aa' }} />
        <span className={styles.copy}>
          <strong>{task.title}</strong>
          <small>
            {task.channel
              ? `${task.channel.guild_name} · #${task.channel.channel_name}`
              : i18n._(msg`Personal task`)}
            {relationship && (
              <>
                <span aria-hidden="true"> · </span>
                <span className={styles.relationship}>{relationship}</span>
              </>
            )}
          </small>
        </span>
        <span className={styles.meta}>{cfg ? i18n._(cfg.label) : task.status}</span>
        {task.thread_id && <MessageOutlined aria-hidden="true" className={styles.thread} />}
      </button>
      <TaskRowActions task={task} onUpdated={onUpdated} onDeleted={onDeleted} />
    </div>
  );
}
